/**
 * Source unique : quelles entrées du menu latéral sont visibles pour quel profil.
 * Utilisé par Sidebar.vue (module 1 par défaut, module 2 via prop menuItems).
 *
 * Règles :
 * - item.roles : correspondance sur user_role (client, user, employe, admin, …). Affichage UI uniquement ;
 *   les droits réels sont imposés par le routeur (profil vérifié serveur) et l’API.
 * - item.permissions : au moins une clé parmi Employe.permissions (voir, lecture_seule_global, valider,
 *   conformite, gestion_utilisateurs — voir gestion.models.Employe).
 * - item.jobs : correspondance sur Employe.role_employe (conseiller, it, saisie, validation…) — distinct
 *   de item.permissions, car un compte IT n'a en pratique que role_employe="it" (permissions vide).
 * - Si roles et permissions sont tous deux définis : visible si (rôle ∈ roles) OU (admin) OU (≥1 permission).
 * - Rôle admin : accès total aux entrées « employé » (même sans permissions en base).
 * - Ni roles ni permissions : visible pour tout utilisateur connecté (ex. tableau de bord).
 *
 * Garde-routes : meta.requiresAnyPermission + requiresRole dans routes.js (mêmes clés que ce menu).
 */
import {
  ChartPieIcon,
  HomeIcon,
  ChatBubbleLeftRightIcon,
  PencilSquareIcon,
  UserGroupIcon,
  DocumentCheckIcon,
  ClipboardDocumentListIcon,
  ClipboardDocumentCheckIcon,
  LightBulbIcon,
  ArrowLeftIcon,
  UserIcon,
  MagnifyingGlassCircleIcon,
  ServerStackIcon,
  ShieldCheckIcon,
} from '@heroicons/vue/24/outline';

/**
 * @param {string} roleRaw - ex. localStorage user_role
 * @param {string[]} permissionsArray - Employe.permissions
 * @param {Array<{ to: string, name: string, icon?: object, roles?: string[]|null, permissions?: string[], jobs?: string[] }>} items
 * @param {string} [roleEmployeRaw] - Employe.role_employe (conseiller, it, saisie, validation…)
 */
export function filterSidebarItemsByProfile(roleRaw, permissionsArray, items, roleEmployeRaw) {
  const role = (roleRaw || '').toString().trim().toLowerCase();
  const perms = Array.isArray(permissionsArray) ? permissionsArray : [];
  const roleEmploye = (roleEmployeRaw || '').toString().trim().toLowerCase();

  // Sécurité : si on ne connaît pas le profil (storage bloqué, session pas encore sync),
  // ne jamais afficher les entrées sensibles. On ne garde que les entrées "communes"
  // (sans roles, sans permissions et sans jobs).
  if (!role && (!perms || perms.length === 0)) {
    return items.filter(
      (item) => !(item.roles?.length) && !(item.permissions?.length) && !(item.jobs?.length)
    );
  }

  const filtered = items.filter((item) => {
    const hasRoles = item.roles && item.roles.length > 0;
    const hasPerms = item.permissions && item.permissions.length > 0;
    const hasJobs = item.jobs && item.jobs.length > 0;
    const roleOk = hasRoles && item.roles.some((r) => String(r).toLowerCase() === role);
    const permOk =
      hasPerms &&
      (role === 'admin' || item.permissions.some((p) => perms.includes(p)));
    const jobOk = hasJobs && item.jobs.some((j) => String(j).toLowerCase() === roleEmploye);

    if (!hasRoles && !hasPerms && !hasJobs) return true;
    return roleOk || permOk || jobOk;
  });

  // Sécurité : pas de fallback "tout afficher" (dangereux si rôle indisponible).
  return filtered;
}

/** Menu latéral — espace Analyse patrimoniale (module 1), par défaut dans Sidebar.vue */
export const MODULE1_SIDEBAR_MENU_ITEMS = [
  // Le tableau de bord est le point de retour de tous les profils : il ouvre la liste.
  { to: '/analyse-patrimoniale/dashboards', name: 'Tableau de bord', icon: HomeIcon, roles: null },
  { to: '/analyse-patrimoniale/gestion-namescan', name: 'NameScan PEP & Sanctions', icon: DocumentCheckIcon, permissions: ['conformite'] , roles: ['admin']},
  { to: '/analyse-patrimoniale/gestion-signatures', name: 'Vérification justificatifs', icon: PencilSquareIcon, permissions: ['conformite'], roles: ['admin']},
  { to: '/analyse-patrimoniale/validation-clients-kyc', name: 'Confirmation Paiement', icon: DocumentCheckIcon, permissions: ['conformite'], roles: ['admin']},
  { to: '/analyse-patrimoniale/statut-paiement-malia', name: 'Statut paiement Malia', icon: MagnifyingGlassCircleIcon, permissions: ['conformite', 'gestion_utilisateurs'] },
  { to: '/analyse-patrimoniale/mon-analyse', name: 'Mon analyse', icon: ClipboardDocumentListIcon, roles: ['client', 'user'] },
  // { to: '/analyse-patrimoniale/profil-risque', name: 'Profil de risque investisseur', icon: ShieldCheckIcon, roles: ['client', 'user'] },
  { to: '/analyse-patrimoniale/analyses-sups', name: 'Résultats d\'analyses', icon: ChartPieIcon, roles: ['client', 'user'] },
  { to: '/analyse-patrimoniale/mes-recommandations', name: 'Mes recommandations', icon: LightBulbIcon, roles: ['client', 'user'] },
  { to: '/analyse-patrimoniale/mes-actions', name: 'Mes actions', icon: ClipboardDocumentCheckIcon, roles: ['client', 'user'] },
  { to: '/analyse-patrimoniale/contact', name: 'Contacter mon conseiller', icon: ChatBubbleLeftRightIcon, roles: ['client', 'user'] },
  { to: '/analyse-patrimoniale/val-analyses', name: 'Validation et génération des analyses', icon: DocumentCheckIcon, permissions: ['voir', 'lecture_seule_global', 'valider'], roles: ['admin'] },
  { to: '/analyse-patrimoniale/suivi-analyses', name: 'Suivi des analyses', icon: ClipboardDocumentCheckIcon, permissions: ['voir', 'lecture_seule_global', 'valider'], roles: ['admin', 'employe'] },
  { to: '/analyse-patrimoniale/gestion-utilisateur', name: 'Gestion des utilisateurs', icon: UserGroupIcon, permissions: ['gestion_utilisateurs'], roles: ['admin', 'employe'] },
  { to: '/analyse-patrimoniale/statistiques', name: 'Statistiques', icon: ChartPieIcon, permissions: ['gestion_utilisateurs'], roles: ['admin'] },
  // Pas de `permissions` ici : ça déclenche le bypass admin de la ligne 61, ce qui affiche
  // le lien pour les admins alors que canAccessItDiagnostic() les bloque ensuite ("pas de
  // bypass admin" — voir DiagnosticIT.vue). `jobs` seul cible role_employe sans ce bypass.
  { to: '/analyse-patrimoniale/diagnostic-it', name: 'Diagnostic IT', icon: ServerStackIcon, jobs: ['it', 'technicien'] },
  // Seul accès IT aux modèles PDF (CGU, contrat) — pas le lien "Gestion des signatures"
  // (roles: ['admin'] ci-dessus), qui donne aussi accès aux pièces d'identité clients.
  { to: '/analyse-patrimoniale/modeles-pdf', name: 'Modèles PDF', icon: PencilSquareIcon, jobs: ['it', 'technicien'] },
  { to: '/acceuil', name: 'Retour à l\'accueil', icon: ArrowLeftIcon, permissions: ['voir', 'lecture_seule_global', 'valider'], roles: ['admin'] },
];

/** Menu latéral — espace Actions mandat (module 2), passé en :menu-items depuis DashboardLayout.vue */
export const MODULE2_SIDEBAR_MENU_ITEMS = [
  { to: '/actions-mandat/dashboards', name: 'Tableau de bord', icon: HomeIcon, roles: ['admin', 'employe', 'saisie', 'validation'] },
  { to: '/actions-mandat/mes-recommandations', name: 'Mes recommandations', icon: LightBulbIcon, roles: ['client', 'user'] },
  { to: '/actions-mandat/client-recommandations', name: 'Recommandations des clients', icon: DocumentCheckIcon, roles: ['employe', 'saisie', 'validation'] },
  { to: '/actions-mandat/all-recommandations', name: 'Toutes les recommandations', icon: DocumentCheckIcon, roles: ['admin'] },
  { to: '/actions-mandat/suivi-recommandations', name: 'Suivi des recommandations', icon: ChartPieIcon, roles: ['admin', 'employe', 'saisie', 'validation'] },
  { to: '/actions-mandat/contact', name: 'Contacter un conseiller', icon: ChatBubbleLeftRightIcon, roles: ['client', 'user'] },
  { to: '/actions-mandat/gestion-utilisateur', name: 'Gestion des utilisateurs', icon: UserGroupIcon, roles: ['admin'] },
  { to: '/actions-mandat/actions-statistiques', name: 'Statistiques', icon: ChartPieIcon, roles: ['admin', 'employe', 'saisie', 'validation'] },
  { to: '/actions-mandat/profil', name: 'Profil', icon: UserIcon, roles: null },
  { to: '/acceuil', name: "Retour à l'accueil", icon: ArrowLeftIcon, roles: ['admin', 'employe', 'validation', 'saisie', 'client', 'user'] },
];
